import React, { FC, useCallback, useEffect, useState } from "react";
import { AppLayoutContext } from "./appLayoutContext";
import { BackButton } from "kw/util/Question/inputs/BackButton";
import { Banner } from "kanopi-components/Banner/Banner";
import { Block } from "baseui/block";
import { Footer } from "kw/util/Footer";
import { gotoParentPage, gotoPreviousPage } from "kw/util/utils";
import { Header } from "kanopi-components/Layout/Header";
import { HeadingXXLarge } from "baseui/typography";
import { HorizontalSpacer } from "kanopi-components/SpaceWrapper/HorizontalSpacer";
import { Icon } from "kanopi-components/icons/Icon";
import { Placeholder } from "kanopi-components/Placeholder";
import { PortalSidebar } from "../PortalSidebar/PortalSidebar";
import { SidePadder } from "kanopi-components/SpaceWrapper/SidePadder";
import { useIsLoggedIn } from "kw/auth/hooks/useIsLoggedIn";
import { useLogout } from "kw/auth/hooks/useLogout";
import { useNavigate, useMatch } from "@reach/router";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useThemeContext } from "kw/themeContext";
import { useUnderBreakpoint } from "kanopi-components/useUnderBreakpoint";
import { useUser } from "kw/auth/useUser";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";

export enum AppLayoutType {
  Empty = "Empty",
  Progress = "Progress",
  Sidebar = "Sidebar",
  Portal = "Portal",
}

export type TAppLayoutProps = {
  userName?: string;
  hideSidebar?: boolean;
  showHeader?: boolean;
  headerText?: string;
  headerTextCenter?: boolean;
  hideBackButton?: boolean;
  layoutType?: AppLayoutType;
  backButtonClick?: () => void;
  children?: React.ReactNode;
};

export const AppLayout: FC<TAppLayoutProps> = ({
  hideSidebar = false,
  showHeader = false,
  headerText = "",
  hideBackButton = false,
  layoutType = AppLayoutType.Empty,
  backButtonClick = gotoParentPage,
  children,
}) => {
  const isDesktop = !useUnderBreakpoint("large");
  const user = useUser();
  const isLoggedIn = useIsLoggedIn();
  const { refreshTheme, themeLoading, context: themeContext } = useThemeContext();
  const [, theme] = useStyletron();
  const userName = user?.firstName
    ? `${user.firstName}${user?.lastName ? ` ${user.lastName}` : ""}`
    : user?.email;
  const pathMatchesPolicyUpdate = useMatch(":policyId/update/details/");

  const handleBackButtonClick = () => {
    if (pathMatchesPolicyUpdate) {
      return gotoPreviousPage();
    }

    return backButtonClick();
  };

  const layoutDefaults = {
    hideSidebar: hideSidebar || (!isDesktop && showHeader),
    showHeader,
    headerText,
    hideBackButton,
    layoutType,
    backButtonClick: handleBackButtonClick,
  };
  const [appLayout, setAppLayout] = useState<TAppLayoutProps>(layoutDefaults);
  const setLayoutOverrides = (overrides: TAppLayoutProps) => {
    // Only replace properties passed by caller
    const layoutStyles = { ...layoutDefaults, ...overrides };
    // SetAppLayout after react batch updates.
    setTimeout(() => {
      setAppLayout(layoutStyles);
    });
  };

  useEffect(() => {
    refreshTheme();
  }, [isLoggedIn]);

  return (
    <AppLayoutContext.Provider
      value={{ context: appLayout, setContext: setLayoutOverrides }}
    >
      <Placeholder loading={themeLoading || !themeContext.theme} fullPage={true}>
        {appLayout?.layoutType === AppLayoutType.Portal && isLoggedIn ? (
          <PortalLayout
            user={user}
            appLayout={appLayout}
            userName={userName}
            hideSidebar={hideSidebar}
            showHeader={showHeader}
            headerText={headerText}
            hideBackButton={hideBackButton}
            backButtonClick={handleBackButtonClick}
          >
            {children}
          </PortalLayout>
        ) : (
          <>
            <HijackBanner user={user} userName={userName} />
            <SidePadder padding="page" paddingTop={theme.sizing.scale850}>
              <VerticalSpacer>
                {appLayout?.showHeader && (
                  <HeadingXXLarge
                    data-testid="layoutHeaderText"
                    marginBottom={0}
                    center={appLayout?.headerTextCenter}
                  >
                    {appLayout.headerText}
                  </HeadingXXLarge>
                )}
                <Block>{children}</Block>
                <Footer />
              </VerticalSpacer>
            </SidePadder>
          </>
        )}
      </Placeholder>
    </AppLayoutContext.Provider>
  );
};

type TPortalLayoutProps = {
  user: TUser;
  userName: string;
  children: React.ReactNode;
  appLayout: TAppLayoutProps;
};
const PortalLayout: React.FC<TPortalLayoutProps> = ({
  user,
  userName,
  children,
  appLayout,
}) => {
  const [, theme] = useStyletron();
  const { logout } = useLogout();
  const expandedLeftNavWidth = "246px";
  const bannerHeight = user?.isHijacked ? theme.sizing.scale1400 : 0;
  const headerHeight = "82px";
  const collapsedLeftNavWidth = theme.sizing.scale1200;
  const contentAreaPaddingTop = bannerHeight
    ? `calc(${bannerHeight} + ${headerHeight})`
    : headerHeight;
  const [containerWidth, setContainerWidth] = useState(
    `calc(100% - ${expandedLeftNavWidth})`
  );
  const headerMainItems = [];
  const headerUserItems = [
    {
      title: "Logout",
      itemId: "#logout",
      icon: <Icon icon="log-out-01" />,
      onSelect: () => {
        logout();
      },
    },
  ];
  const [containerPadding, setContainerPadding] = useState(expandedLeftNavWidth);
  const onLeftNavToggle = (open: boolean) => {
    setContainerWidth(
      open
        ? `calc(100% - ${expandedLeftNavWidth})`
        : `calc(100% - ${collapsedLeftNavWidth})`
    );
    setContainerPadding(open ? expandedLeftNavWidth : collapsedLeftNavWidth);
  };

  return (
    <>
      <Block
        display="flex"
        width="100%"
        flexDirection={["row", "column", "row", "row"]}
      >
        {!appLayout?.hideSidebar && (
          <Block
            display="flex"
            position="fixed"
            top="0"
            width={["auto", "100%", "auto", "auto"]}
            overrides={{
              Block: { style: { zIndex: theme.zIndex.fixedComponent } },
            }}
          >
            <PortalSidebar
              userName={userName}
              allowAdminPortalAccess={user?.allowAdminPortalAccess}
              allowReportAccess={user?.allowReportAccess}
              onOpenClose={onLeftNavToggle}
              mainItems={headerMainItems}
              userItems={headerUserItems}
            />
          </Block>
        )}
        <Block
          display="flex"
          flexDirection="column"
          flex="1"
          justifyContent="space-between"
          minHeight="100vh"
          paddingLeft={[containerPadding, 0, containerPadding, containerPadding]}
          width={[containerWidth, "100%", containerWidth, containerWidth]}
          overrides={{
            Block: {
              style: {
                backgroundColor: "#fafaf7",
              },
            },
          }}
        >
          <Block
            backgroundColor={theme.colors.backgroundPrimary}
            display={["none", "none", "block", "block"]}
            width={containerWidth}
            position="fixed"
            top="0"
            overrides={{
              Block: { style: { zIndex: theme.zIndex.fixedComponent } },
            }}
          >
            <HijackBanner user={user} userName={userName} />
            <SidePadder>
              <Header
                userName={userName}
                mainItems={headerMainItems}
                userItems={headerUserItems}
              />
            </SidePadder>
          </Block>
          <SidePadder padding="page" paddingTop={contentAreaPaddingTop}>
            <VerticalSpacer gap="inner-section">
              {(!appLayout?.hideBackButton || appLayout?.showHeader) && (
                <HorizontalSpacer
                  justifyContent={
                    !appLayout?.hideBackButton
                      ? "space-between"
                      : appLayout?.headerTextCenter
                      ? "center"
                      : "left"
                  }
                >
                  {!appLayout?.hideBackButton && (
                    <Block
                      display="flex"
                      alignContent="center"
                      marginTop={theme.sizing.scale1000}
                    >
                      <BackButton onClick={appLayout?.backButtonClick} />
                    </Block>
                  )}
                  {appLayout?.showHeader && (
                    <HeadingXXLarge
                      data-testid="layoutHeaderText"
                      marginBottom={0}
                      justifyContent={appLayout?.headerTextCenter ? "center" : "left"}
                    >
                      {appLayout.headerText}
                    </HeadingXXLarge>
                  )}
                  {/* Empty block with same width as back button to center header text */}
                  {!appLayout?.hideBackButton && (
                    <Block width={theme.sizing.scale950}></Block>
                  )}
                </HorizontalSpacer>
              )}
              <Block
                paddingTop={
                  appLayout?.hideBackButton && !appLayout?.showHeader
                    ? theme.sizing.scale1000
                    : 0
                }
                role="main"
              >
                {children}
              </Block>
            </VerticalSpacer>
          </SidePadder>
          <SidePadder padding="section">
            <Block
              width="100%"
              display="flex"
              justifyContent="right"
              alignItems="center"
              role="contentinfo"
            >
              <Footer inline={true} />
            </Block>
          </SidePadder>
        </Block>
      </Block>
    </>
  );
};

const HijackBanner: FC<{ user: TUser; userName: string }> = ({ user, userName }) => {
  const navigate = useNavigate();
  const onReleaseUserClick = useCallback(() => {
    navigate("/user-hijacking/release");
  }, []);
  return (
    user?.isHijacked && (
      <Banner
        data-testid="hijackUserBanner"
        message={`You're using the app on behalf of
    ${userName || "[Name unknown]"}`}
        buttonText="Return to portal"
        onButtonClick={onReleaseUserClick}
        backgroundColor="#89877E"
      />
    )
  );
};
